import { useEffect, useState, type FormEvent } from "react";

import { ApiError, api, type SessionEnd } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { countyName } from "../lib/counties";
import { FORGOT_PATH } from "../lib/route";
import { GoogleButton } from "./GoogleButton";
import { Link, useSearchParam } from "../router";

type Mode = "login" | "signup";

/**
 * Why the form is being shown to someone who was already signed in.
 *
 * The two cases are kept apart deliberately. "Expired" is routine. "Superseded"
 * is the only notice a customer ever gets that their credentials are in use
 * somewhere they did not authorise, so it names the cause and the rule rather
 * than hiding behind "please sign in again".
 */
const SESSION_END_NOTICE: Record<SessionEnd, string> = {
  expired: "Your session expired. Sign in again to pick up where you left off.",
  superseded:
    "You were signed out because this account signed in on another device. " +
    "A subscription covers one person — if that wasn't you, change your " +
    "password and tell us at support@dtrworks.net.",
};

export function Gate() {
  const { signIn, signUp, signInWithGoogle, sessionEnd } = useAuth();
  // The county the visitor was opening when they hit this. It stays in the URL
  // through sign-in, so the dashboard renders that county straight after —
  // there is nothing to remember and nothing to redirect to.
  const county = useSearchParam("county");
  // Set by the reset page on its way here. The form is where a reset lands —
  // it ends every session, so there is nowhere else to go — and arriving with
  // no word of what happened leaves someone unsure whether the password they
  // just chose is the one to type.
  const justReset = useSearchParam("reset") === "done";
  // A county landing page sends people here to *create* an account, and an
  // ad click that lands on "Sign in" with the signup link in small print
  // underneath is a click half wasted. The URL says which form to open;
  // sign-in stays the default for everyone who arrives on their own.
  const wantsSignup = useSearchParam("mode") === "signup";
  const [mode, setMode] = useState<Mode>(wantsSignup ? "signup" : "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  // Whether the Google button is going to draw anything, which is only known
  // once /api/auth/config answers. Drives the divider above it.
  const [googleEnabled, setGoogleEnabled] = useState(false);
  // Whether a reset link can actually be delivered on this deployment. Read
  // from the same endpoint the Google button reads — a second request for two
  // booleans held in application state, which is cheaper than threading one
  // fetch through a component whose job is Google's script.
  //
  // Defaults to false, so a config call that fails draws no link rather than
  // one that leads to a form the server will refuse.
  const [resetEnabled, setResetEnabled] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .authConfig(controller.signal)
      .then((config) => setResetEnabled(config.password_reset_enabled))
      .catch(() => {
        /* not configured, or the server is down; the form still works */
      });
    return () => controller.abort();
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await signIn(email, password);
      else await signUp(email, password, accepted);
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "Something went wrong.",
      );
    } finally {
      setBusy(false);
    }
  }

  function toggle() {
    setMode(mode === "login" ? "signup" : "login");
    setAccepted(false);
    setError("");
  }

  return (
    <div className="gate">
      <div className="card">
        <h1>
          <span>D</span>is<span>TR</span>ess Signal
        </h1>
        {county ? (
          <p className="lede">
            <b>Opening {countyName(county) || county} County.</b> The buildings
            behind it need an account — a free one shows the ten worst.
          </p>
        ) : (
          <p className="lede">
            Structural distress across Florida, pulled from county records.
            Buildings overdue for milestone inspection, and parcels already
            carrying open violations.
          </p>
        )}

        {/* The reset outcome outranks the session notice, and by a lot: a
            reset *causes* a session to end, so both are true at once and
            "you were signed out on another device" would read as a warning
            about something the person just did themselves. */}
        {justReset && !error && (
          <div className="err" role="status">
            Password changed. Sign in with your new one — every device that was
            signed into this account has been signed out.
          </div>
        )}

        {/* Above the form and above `error`, because it explains why the form
            is here at all. A failed attempt at the form below replaces nothing
            — the two messages are about different things. */}
        {sessionEnd && !error && !justReset && (
          <div className="err" role="status">
            {SESSION_END_NOTICE[sessionEnd]}
          </div>
        )}

        {error && (
          <div className="err" role="alert">
            {error}
          </div>
        )}

        <form onSubmit={submit}>
          <label className="field">
            <span>Email</span>
            <input
              type="email"
              value={email}
              required
              autoComplete="username"
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Password</span>
            <input
              type="password"
              value={password}
              required
              minLength={8}
              autoComplete={
                mode === "login" ? "current-password" : "new-password"
              }
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          {mode === "signup" && (
            <label className="check accept">
              <input
                type="checkbox"
                checked={accepted}
                required
                onChange={(event) => setAccepted(event.target.checked)}
              />
              <span>
                I accept the{" "}
                <Link to="/terms" newTab>
                  Terms of Service
                </Link>
                ,{" "}
                <Link to="/privacy" newTab>
                  Privacy Policy
                </Link>
                ,{" "}
                <Link to="/acceptable-use" newTab>
                  Acceptable Use Policy
                </Link>{" "}
                and{" "}
                <Link to="/data-licence" newTab>
                  Data Licence
                </Link>
                .
              </span>
            </label>
          )}

          <div style={{ height: 20 }} />
          <button className="btn" disabled={busy || (mode === "signup" && !accepted)}>
            {busy ? "Working…" : mode === "login" ? "Sign in" : "Create account"}
          </button>
        </form>

        {/* Under the form and only on the sign-in side: someone creating an
            account has no password to have forgotten. Drawn only where a reset
            can be delivered — an offered link that leads to a form the server
            refuses is worse than no link, which is the rule the Google button
            below follows too. */}
        {mode === "login" && resetEnabled && (
          <p className="switch">
            <Link to={FORGOT_PATH} className="linkbtn">
              Forgotten your password?
            </Link>
          </p>
        )}

        {/* Below the form, not above it. Email and password are the account
            this product actually issues; Google is an alternative way into the
            same one, and leading with someone else's button asks a visitor to
            make a choice before they have been told what they are signing into.
            The divider renders only when Google is configured — GoogleButton
            draws nothing without a client id, and a rule captioned "Or" with
            nothing after it is worse than no rule at all. */}
        {googleEnabled && <div className="or" aria-hidden="true">Or</div>}
        <GoogleButton
          text={mode === "signup" ? "signup_with" : "signin_with"}
          onReady={setGoogleEnabled}
          onCredential={(credential) => {
            setError("");
            setBusy(true);
            signInWithGoogle(credential)
              .catch((cause) =>
                setError(
                  cause instanceof ApiError
                    ? cause.message
                    : "Google sign-in didn't work.",
                ),
              )
              .finally(() => setBusy(false));
          }}
        />

        <p className="switch">
          {mode === "login" ? "No account yet? " : "Already have one? "}
          <button type="button" className="linkbtn" onClick={toggle}>
            {mode === "login" ? "Create one free" : "Sign in"}
          </button>
        </p>

        {/* Somewhere to go that isn't this form. Coverage is the one page that
            answers "do you even have my county?" without an account. */}
        <p className="switch">
          Not sure yet? Read the{" "}
          <Link to="/coverage" className="linkbtn">
            coverage map
          </Link>{" "}
          first — county by county, no account needed.
        </p>
      </div>
    </div>
  );
}
